import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Avatar,
  TablePagination,
  TextField,
  MenuItem,
  Stack
} from '@mui/material';
import {
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  QrCodeScanner,
  Assignment,
  Download
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import SkeletonLoader from '../Layout/SkeletonLoader';
import { getRfidScanResults, exportRfidScanResult } from '../../services/api';
import CopyableTag from '../Shared/CopyableTag';

// Mock data for inventory count history (fallback)
const mockInventoryCountHistory = [
  {
    id: 'IC-001',
    clientId: 338669,
    clientName: 'Client 338669',
    performedBy: 'John Doe',
    scanTimestamp: '2025-10-27T10:30:00',
    totalSKU: 45,
    scannedSKU: 43,
    totalRfidTags: 156,
    scannedRfidTags: 148,
    unassignedTags: 8,
    matchesWarehouse: false,
    deviceId: 'RFID-READER-001',
    scanDuration: 1800, // seconds
    summary: {
      totalTagsScanned: 148,
      assignedTagsCount: 140,
      unassignedTagsCount: 8,
      uniqueSKUsFound: 43,
      missingSKUsCount: 2,
      completionStatus: {
        itemsComplete: true,
        skusComplete: false,
        fullyComplete: false
      }
    },
    skuResults: [
      {
        skuCode: 'SKU-001',
        skuData: {
          name: 'Product A',
          sku: 'SKU-001',
          image: null,
          totalQty: 10,
          onHand: 10
        },
        scannedTags: [
          { tagId: 'TAG001', timestamp: '2025-10-27T10:35:00' },
          { tagId: 'TAG002', timestamp: '2025-10-27T10:35:05' }
        ],
        missingTags: [],
        scannedCount: 10,
        missingCount: 0,
        expectedCount: 10
      },
      {
        skuCode: 'SKU-002',
        skuData: {
          name: 'Product B',
          sku: 'SKU-002',
          image: null,
          totalQty: 15,
          onHand: 15
        },
        scannedTags: [
          { tagId: 'TAG010', timestamp: '2025-10-27T10:40:00' }
        ],
        missingTags: ['TAG011', 'TAG012'],
        scannedCount: 13,
        missingCount: 2,
        expectedCount: 15
      }
    ],
    missingSKUs: [
      {
        skuCode: 'SKU-003',
        skuData: {
          name: 'Product C - Missing',
          sku: 'SKU-003',
          image: null,
          totalQty: 8,
          onHand: 8
        },
        expectedTags: ['TAG020', 'TAG021', 'TAG022'],
        missingTags: ['TAG020', 'TAG021', 'TAG022'],
        scannedCount: 0,
        missingCount: 3,
        expectedCount: 3
      },
      {
        skuCode: 'SKU-004',
        skuData: {
          name: 'Product D - Missing',
          sku: 'SKU-004',
          image: null,
          totalQty: 5,
          onHand: 5
        },
        expectedTags: ['TAG030', 'TAG031'],
        missingTags: ['TAG030', 'TAG031'],
        scannedCount: 0,
        missingCount: 2,
        expectedCount: 2
      }
    ],
    unassignedTagsList: [
      { tagId: 'UNASSIGNED-001', timestamp: '2025-10-27T10:45:00' },
      { tagId: 'UNASSIGNED-002', timestamp: '2025-10-27T10:45:10' }
    ],
    allScannedTagsList: ['TAG001', 'TAG002', 'TAG010', 'UNASSIGNED-001', 'UNASSIGNED-002']
  },
  {
    id: 'IC-002',
    clientId: 338614,
    clientName: 'Client 338614',
    performedBy: 'Jane Smith',
    scanTimestamp: '2025-10-26T14:15:00',
    totalSKU: 38,
    scannedSKU: 38,
    totalRfidTags: 120,
    scannedRfidTags: 120,
    unassignedTags: 0,
    matchesWarehouse: true,
    deviceId: 'RFID-READER-002',
    scanDuration: 1200,
    summary: {
      totalTagsScanned: 120,
      assignedTagsCount: 120,
      unassignedTagsCount: 0,
      uniqueSKUsFound: 38,
      missingSKUsCount: 0,
      completionStatus: {
        itemsComplete: true,
        skusComplete: true,
        fullyComplete: true
      }
    },
    skuResults: [
      {
        skuCode: 'SKU-003',
        skuData: {
          name: 'Product C',
          sku: 'SKU-003',
          image: null,
          totalQty: 8,
          onHand: 8
        },
        scannedTags: [
          { tagId: 'TAG100', timestamp: '2025-10-26T14:20:00' }
        ],
        missingTags: [],
        scannedCount: 8,
        missingCount: 0,
        expectedCount: 8
      }
    ],
    missingSKUs: [],
    unassignedTagsList: [],
    allScannedTagsList: ['TAG100', 'TAG101', 'TAG102']
  },
  {
    id: 'IC-003',
    clientId: 338669,
    clientName: 'Client 338669',
    performedBy: 'Mike Johnson',
    scanTimestamp: '2025-10-25T09:45:00',
    totalSKU: 52,
    scannedSKU: 48,
    totalRfidTags: 180,
    scannedRfidTags: 165,
    unassignedTags: 15,
    matchesWarehouse: false,
    deviceId: 'RFID-READER-001',
    scanDuration: 2100,
    summary: {
      totalTagsScanned: 165,
      assignedTagsCount: 150,
      unassignedTagsCount: 15,
      uniqueSKUsFound: 48,
      missingSKUsCount: 4,
      completionStatus: {
        itemsComplete: false,
        skusComplete: false,
        fullyComplete: false
      }
    },
    skuResults: [],
    missingSKUs: [],
    unassignedTagsList: [],
    allScannedTagsList: []
  },
  {
    id: 'IC-004',
    clientId: 338614,
    clientName: 'Client 338614',
    performedBy: 'Sarah Williams',
    scanTimestamp: '2025-10-24T16:20:00',
    totalSKU: 41,
    scannedSKU: 41,
    totalRfidTags: 135,
    scannedRfidTags: 135,
    unassignedTags: 0,
    matchesWarehouse: true,
    deviceId: 'RFID-READER-003',
    scanDuration: 1350,
    summary: {
      totalTagsScanned: 135,
      assignedTagsCount: 135,
      unassignedTagsCount: 0,
      uniqueSKUsFound: 41,
      missingSKUsCount: 0,
      completionStatus: {
        itemsComplete: true,
        skusComplete: true,
        fullyComplete: true
      }
    },
    skuResults: [],
    missingSKUs: [],
    unassignedTagsList: [],
    allScannedTagsList: []
  },
  {
    id: 'IC-005',
    clientId: 338669,
    clientName: 'Client 338669',
    performedBy: 'David Brown',
    scanTimestamp: '2025-10-23T11:00:00',
    totalSKU: 47,
    scannedSKU: 45,
    totalRfidTags: 160,
    scannedRfidTags: 152,
    unassignedTags: 8,
    matchesWarehouse: false,
    deviceId: 'RFID-READER-002',
    scanDuration: 1650,
    summary: {
      totalTagsScanned: 152,
      assignedTagsCount: 144,
      unassignedTagsCount: 8,
      uniqueSKUsFound: 45,
      missingSKUsCount: 2,
      completionStatus: {
        itemsComplete: false,
        skusComplete: false,
        fullyComplete: false
      }
    },
    skuResults: [],
    missingSKUs: [],
    unassignedTagsList: [],
    allScannedTagsList: []
  }
];

const getInitials = (name = '') => {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return initials || '?';
};

const getCurrentWeekRange = () => {
  const now = new Date();
  const currentDay = now.getDay() === 0 ? 7 : now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - currentDay + 1);
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return { startOfWeek, endOfWeek };
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localISOTime = new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  return localISOTime;
};

const toIsoString = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return date.toISOString();
};

const defaultSummary = {
  totalTagsScanned: 0,
  assignedTagsCount: 0,
  unassignedTagsCount: 0,
  uniqueSKUsFound: 0
};

const InventoryCountHistory = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalTab, setDetailModalTab] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { startOfWeek, endOfWeek } = getCurrentWeekRange();
  const [filters, setFilters] = useState({
    startDate: formatDateForInput(startOfWeek),
    endDate: formatDateForInput(endOfWeek),
    clientId: ''
  });
  const [filterError, setFilterError] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [clientNameMap, setClientNameMap] = useState({});
  const [downloadingId, setDownloadingId] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const normalizedSelectedRecord = useMemo(() => {
    if (!selectedRecord) return null;
    return {
      ...selectedRecord,
      summary: { ...defaultSummary, ...(selectedRecord.summary || {}) },
      skuResults: selectedRecord.skuResults || [],
      missingSKUs: selectedRecord.missingSKUs || [],
      unassignedTagsList: selectedRecord.unassignedTagsList || [],
      allScannedTagsList: selectedRecord.allScannedTagsList || []
    };
  }, [selectedRecord]);

  const scannedSkuResults = useMemo(() => {
    if (!normalizedSelectedRecord) return [];
    return normalizedSelectedRecord.skuResults.filter((sku) => {
      const scannedCount = sku.scannedCount ?? sku.scannedTags?.length ?? 0;
      return scannedCount > 0;
    });
  }, [normalizedSelectedRecord]);

  const missingSkuResults = useMemo(() => {
    if (!normalizedSelectedRecord) return [];
    const filtered = normalizedSelectedRecord.skuResults.filter((sku) => {
      const scannedCount = sku.scannedCount ?? sku.scannedTags?.length ?? 0;
      return scannedCount === 0;
    });
    if (filtered.length > 0) {
      return filtered;
    }
    return normalizedSelectedRecord.missingSKUs || [];
  }, [normalizedSelectedRecord]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const rawMeta = localStorage.getItem('appMetaData');
      if (!rawMeta) return;
      const parsedMeta = JSON.parse(rawMeta);
      const stores = parsedMeta?.storeList || [];
      const normalizedStores = stores
        .filter(store => typeof store.id === 'number' && store.id !== -1)
        .map(store => ({ id: store.id, name: store.name || `Client ${store.id}` }));
      setClientOptions(normalizedStores);
      const nameDictionary = normalizedStores.reduce((acc, store) => {
        acc[store.id] = store.name;
        return acc;
      }, {});
      setClientNameMap(nameDictionary);
    } catch (error) {
      console.error('Failed to parse client metadata from localStorage:', error);
    }
  }, []);

  // Load history data from API
  useEffect(() => {
    const loadHistoryData = async () => {
      if (!filters.startDate || !filters.endDate) {
        setFilterError('Please select both start and end dates.');
        return;
      }
      if (new Date(filters.startDate) > new Date(filters.endDate)) {
        setFilterError('Start date must be before end date.');
        return;
      }
      setFilterError('');
      setIsLoading(true);
      try {
        // Call API with pagination
        const response = await getRfidScanResults({
          page: page + 1, // API uses 1-based pagination
          limit: rowsPerPage,
          startDate: toIsoString(filters.startDate),
          endDate: toIsoString(filters.endDate),
          clientId: filters.clientId ? Number(filters.clientId) : undefined
        });
        
        // Transform API response to match UI format
        const transformedData = response.data.map(item => {
          const clientId = item.scanInfo?.clientId || item.metadata?.clientId || item.client?.id || 'N/A';
          const fallbackClientName =
            item.scanInfo?.clientName ||
            item.metadata?.clientName ||
            item.client?.name ||
            item.clientInfo?.name ||
            (clientId !== 'N/A' ? `Client ${clientId}` : 'Unknown client');
          const performer =
            item.scannedBy ||
            item.scanInfo?.performedBy ||
            item.metadata?.performedBy ||
            'Unknown';

          return {
            id: item.id,
            clientId,
            clientName: fallbackClientName,
            performedBy: performer,
            scanTimestamp: item.dateTime || item.createdAt,
          totalSKU: item.metadata?.totalSKU || 0,
          scannedSKU: item.summary?.uniqueSKUsFound || 0,
          totalRfidTags: item.metadata?.totalUnit || 0,
          scannedRfidTags: item.summary?.totalTagsScanned || 0,
          unassignedTags: item.summary?.unassignedTagsCount || 0,
          matchesWarehouse: item.status === 'Match',
          deviceId: item.scanInfo?.deviceId || 'N/A',
          scanDuration: item.scanInfo?.scanDuration || 0,
          summary: item.summary || {},
          skuResults: item.skuResults || [],
          missingSKUs: item.missingSKUs || [],
          unassignedTagsList: item.unassignedTags || [],
          allScannedTagsList: item.allScannedTags?.map(t => t.tagId) || []
          };
        });
        
        setHistoryData(transformedData);
        setTotalRecords(response.total || 0);
        setTotalPages(response.totalPages || 0);
      } catch (error) {
        console.error('Error loading inventory count history:', error);
        // Fallback to mock data if API fails
        setHistoryData(mockInventoryCountHistory);
        setTotalRecords(mockInventoryCountHistory.length);
        setTotalPages(Math.ceil(mockInventoryCountHistory.length / rowsPerPage));
      } finally {
        setIsLoading(false);
      }
    };

    loadHistoryData();
  }, [page, rowsPerPage, filters.startDate, filters.endDate, filters.clientId]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleResetFilters = () => {
    const defaultRange = getCurrentWeekRange();
    setFilters({
      startDate: formatDateForInput(defaultRange.startOfWeek),
      endDate: formatDateForInput(defaultRange.endOfWeek),
      clientId: ''
    });
    setPage(0);
  };

  const handleViewDetails = (record) => {
    navigate(`/inventory-count-history/${record.id}`);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedRecord(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadReport = async (recordId) => {
    if (!recordId) return;
    try {
      setDownloadingId(recordId);
      const fileBlob = await exportRfidScanResult(recordId);
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rfid-scan-${recordId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download RFID scan report:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {isLoading ? (
        <SkeletonLoader type="table" count={5} />
      ) : (
        <>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                Inventory Count History
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                View past inventory count records and their details
              </Typography>
            </Box>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Start date"
                      type="datetime-local"
                      fullWidth
                      value={filters.startDate}
                      onChange={(event) => handleFilterChange('startDate', event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="End date"
                      type="datetime-local"
                      fullWidth
                      value={filters.endDate}
                      onChange={(event) => handleFilterChange('endDate', event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      label="Client"
                      fullWidth
                      value={filters.clientId}
                      onChange={(event) => handleFilterChange('clientId', event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">All clients</MenuItem>
                      {clientOptions.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="text" color="primary" onClick={handleResetFilters}>
                      Reset
                    </Button>
                  </Grid>
                </Grid>
                {filterError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {filterError}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Scan Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Performed By</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} align="center">SKU Count</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} align="center">RFID Tags</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.map((record) => (
                        <TableRow
                          key={record.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#f8fafc'
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(record.scanTimestamp)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              Duration: {formatDuration(record.scanDuration)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const clientDisplayName =
                                clientNameMap?.[Number(record.clientId)] || record.clientName;
                              return (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {clientDisplayName}
                                  </Typography>
                                  {record.clientId && record.clientId !== 'N/A' && (
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                      ID: {record.clientId}
                                    </Typography>
                                  )}
                                </>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                                {getInitials(record.performedBy)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {record.performedBy}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {record.scannedSKU} / {record.totalSKU}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    record.totalSKU > 0 && record.scannedSKU === record.totalSKU
                                      ? '#16a34a'
                                      : '#f59e0b'
                                }}
                              >
                                {record.totalSKU
                                  ? Math.round((record.scannedSKU / record.totalSKU) * 100)
                                  : 0}
                                %
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {record.scannedRfidTags} / {record.totalRfidTags}
                              </Typography>
                              {record.unassignedTags > 0 && (
                                <Typography variant="caption" sx={{ color: '#dc2626' }}>
                                  {record.unassignedTags} unassigned
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              icon={record.matchesWarehouse ? <CheckCircle /> : <Cancel />}
                              label={record.matchesWarehouse ? 'Match' : 'Mismatch'}
                              size="small"
                              sx={{
                                backgroundColor: record.matchesWarehouse ? '#dcfce7' : '#fee2e2',
                                color: record.matchesWarehouse ? '#166534' : '#991b1b',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                border: `1px solid ${
                                  record.matchesWarehouse ? '#bbf7d0' : '#fca5a5'
                                }`
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(record)}
                                sx={{
                                  color: '#3b82f6',
                                  '&:hover': {
                                    backgroundColor: '#eff6ff'
                                  }
                                }}
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadReport(record.id)}
                                disabled={downloadingId === record.id}
                                sx={{
                                  color: '#16a34a',
                                  '&:hover': {
                                    backgroundColor: '#ecfdf5'
                                  }
                                }}
                              >
                                {downloadingId === record.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <Download />
                                )}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          </motion.div>
        </>
      )}

      {/* Detail Modal */}
      {normalizedSelectedRecord && (
        <Dialog
          open={showDetailModal}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Inventory Count Details</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {normalizedSelectedRecord.id} • {formatDate(normalizedSelectedRecord.scanTimestamp)}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseModal}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {/* Summary Cards */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Inventory Count Summary
              </Typography>
              
              {/* Basic Information */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Client
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {normalizedSelectedRecord.clientName}
                    </Typography>
                    {normalizedSelectedRecord.clientId && normalizedSelectedRecord.clientId !== 'N/A' && (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ID: {normalizedSelectedRecord.clientId}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Performed By
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {normalizedSelectedRecord.performedBy}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Device ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {normalizedSelectedRecord.deviceId}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Scan Duration
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {formatDuration(normalizedSelectedRecord.scanDuration)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Metrics */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' },
                gap: 2, 
                mb: 2
              }}>
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: '#1e40af',
                    textAlign: 'center'
                  }}>
                    {normalizedSelectedRecord.summary.totalTagsScanned}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#64748b',
                    textAlign: 'center',
                    mt: 1
                  }}>
                    Total Tags Scanned
                  </Typography>
                </Paper>
                
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0'
                }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: '#16a34a',
                    textAlign: 'center'
                  }}>
                    {normalizedSelectedRecord.summary.assignedTagsCount}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#64748b',
                    textAlign: 'center',
                    mt: 1
                  }}>
                    Assigned Tags
                  </Typography>
                </Paper>

                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: normalizedSelectedRecord.summary.unassignedTagsCount > 0 ? '#fef2f2' : '#f8fafc',
                  border: normalizedSelectedRecord.summary.unassignedTagsCount > 0 ? '1px solid #fca5a5' : '1px solid #e2e8f0'
                }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: normalizedSelectedRecord.summary.unassignedTagsCount > 0 ? '#dc2626' : '#64748b',
                    textAlign: 'center'
                  }}>
                    {normalizedSelectedRecord.summary.unassignedTagsCount}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#64748b',
                    textAlign: 'center',
                    mt: 1
                  }}>
                    Unassigned Tags
                  </Typography>
                </Paper>
                
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0'
                }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: '#16a34a',
                    textAlign: 'center'
                  }}>
                    {normalizedSelectedRecord.summary.uniqueSKUsFound}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#64748b',
                    textAlign: 'center',
                    mt: 1
                  }}>
                    Unique SKUs Found
                  </Typography>
                </Paper>
                
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: normalizedSelectedRecord.matchesWarehouse ? '#f0fdf4' : '#fef2f2',
                  border: normalizedSelectedRecord.matchesWarehouse ? '1px solid #bbf7d0' : '1px solid #fca5a5'
                }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: normalizedSelectedRecord.matchesWarehouse ? '#16a34a' : '#dc2626',
                    textAlign: 'center'
                  }}>
                    {normalizedSelectedRecord.matchesWarehouse ? <CheckCircle sx={{ fontSize: '2rem' }} /> : <Cancel sx={{ fontSize: '2rem' }} />}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#64748b',
                    textAlign: 'center',
                    mt: 1
                  }}>
                    Warehouse Status
                  </Typography>
                </Paper>
              </Box>

              {/* Comparison with Metadata */}
              <Paper sx={{ 
                p: 2, 
                backgroundColor: normalizedSelectedRecord.matchesWarehouse ? '#f0fdf4' : '#fefce8', 
                border: normalizedSelectedRecord.matchesWarehouse ? '1px solid #bbf7d0' : '1px solid #fde047', 
                mt: 2 
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {normalizedSelectedRecord.matchesWarehouse ? 
                    '✅ Inventory Count Complete!' : 
                    'Comparison with Warehouse Metadata:'
                  }
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ 
                    color: normalizedSelectedRecord.scannedSKU === normalizedSelectedRecord.totalSKU ? '#16a34a' : '#64748b',
                    fontWeight: normalizedSelectedRecord.scannedSKU === normalizedSelectedRecord.totalSKU ? 600 : 400
                  }}>
                    SKUs Found: {normalizedSelectedRecord.scannedSKU} / {normalizedSelectedRecord.totalSKU}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: normalizedSelectedRecord.scannedRfidTags === normalizedSelectedRecord.totalRfidTags ? '#16a34a' : '#64748b',
                    fontWeight: normalizedSelectedRecord.scannedRfidTags === normalizedSelectedRecord.totalRfidTags ? 600 : 400
                  }}>
                    Tags Found: {normalizedSelectedRecord.scannedRfidTags} / {normalizedSelectedRecord.totalRfidTags}
                  </Typography>
                  {normalizedSelectedRecord.unassignedTags > 0 && (
                    <Typography variant="body2" sx={{ 
                      color: '#dc2626',
                      fontWeight: 600
                    }}>
                      Unassigned Tags: {normalizedSelectedRecord.unassignedTags}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>

            <Divider />

            {/* Modal Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={detailModalTab}
                onChange={(e, newValue) => setDetailModalTab(newValue)}
                sx={{ px: 3 }}
              >
                <Tab label="SKU Details" />
                <Tab label="Missing SKUs" />
                <Tab label="Unassigned Tags" />
                <Tab label="All Tags" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 3 }}>
              {detailModalTab === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Scanned Products (SKUs)
                  </Typography>
                  
                  {scannedSkuResults.length > 0 ? (
                    scannedSkuResults.map((skuResult) => (
                      <Paper key={skuResult.skuCode} sx={{ 
                        mb: 2, 
                        p: 2, 
                        border: '1px solid #e5e7eb',
                        borderRadius: 2
                      }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          {/* SKU Image */}
                          {skuResult.skuData.image && (
                            <Box sx={{ 
                              width: 80, 
                              height: 80, 
                              flexShrink: 0,
                              border: '1px solid #e5e7eb',
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}>
                              <img 
                                src={skuResult.skuData.image} 
                                alt={skuResult.skuData.name}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover' 
                                }}
                              />
                            </Box>
                          )}
                          
                          {/* SKU Info */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {skuResult.skuData.name || skuResult.skuCode}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                              SKU: {skuResult.skuCode}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Chip 
                                label={`${skuResult.scannedCount} Scanned`}
                                color="success"
                                size="small"
                              />
                              {skuResult.missingCount > 0 && (
                                <Chip 
                                  label={`${skuResult.missingCount} Missing`}
                                  color="warning"
                                  size="small"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Scanned Tags - Only show count */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Scanned Tags: {skuResult.scannedTags?.length || skuResult.scannedCount} tags
                          </Typography>
                        </Box>
                        
                        {/* Missing Tags - Show detailed list */}
                        {skuResult.missingTags && skuResult.missingTags.length > 0 && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#f59e0b' }}>
                              Missing Tags ({skuResult.missingTags.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {skuResult.missingTags.map((tagId, index) => (
                                <CopyableTag
                                  key={index}
                                  text={tagId}
                                  variant="chip"
                                  color="warning"
                                  chipVariant="outlined"
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: '#6b7280',
                      textAlign: 'center',
                      py: 2
                    }}>
                      No scanned SKUs available for this count
                    </Typography>
                  )}
                </Box>
              )}

              {detailModalTab === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Missing SKUs (Not Scanned)
                  </Typography>
                  
                  {missingSkuResults.length > 0 ? (
                    missingSkuResults.map((skuResult) => (
                      <Paper key={skuResult.skuCode} sx={{ 
                        mb: 2, 
                        p: 2, 
                        border: '1px solid #fca5a5',
                        borderRadius: 2,
                        backgroundColor: '#fef2f2'
                      }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          {/* SKU Image */}
                          {skuResult.skuData.image && (
                            <Box sx={{ 
                              width: 80, 
                              height: 80, 
                              flexShrink: 0,
                              border: '1px solid #fca5a5',
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}>
                              <img 
                                src={skuResult.skuData.image} 
                                alt={skuResult.skuData.name}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover' 
                                }}
                              />
                            </Box>
                          )}
                          
                          {/* SKU Info */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {skuResult.skuData.name || skuResult.skuCode}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                              SKU: {skuResult.skuCode}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Expected Tags - Show detailed list */}
                        {skuResult.expectedTags && skuResult.expectedTags.length > 0 && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                              Expected Tags ({skuResult.expectedTags.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {skuResult.expectedTags.map((tagId, index) => (
                                <CopyableTag
                                  key={index}
                                  text={tagId}
                                  variant="chip"
                                  color="error"
                                  chipVariant="outlined"
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {skuResult.missingTags && skuResult.missingTags.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#f59e0b' }}>
                              Missing Tags ({skuResult.missingTags.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {skuResult.missingTags.map((tagId, index) => (
                                <CopyableTag
                                  key={index}
                                  text={tagId}
                                  variant="chip"
                                  color="warning"
                                  chipVariant="outlined"
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: '#6b7280',
                      textAlign: 'center',
                      py: 2
                    }}>
                      All SKUs in warehouse have been scanned
                    </Typography>
                  )}
                </Box>
              )}

              {detailModalTab === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Unassigned RFID Tags
                  </Typography>
                  
                  {normalizedSelectedRecord.unassignedTagsList && normalizedSelectedRecord.unassignedTagsList.length > 0 ? (
                    <List sx={{ 
                      maxHeight: 300, 
                      overflow: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: 1
                    }}>
                      {normalizedSelectedRecord.unassignedTagsList.map((tag, index) => (
                        <ListItem key={tag.tagId} sx={{ borderBottom: '1px solid #f3f4f6' }}>
                          <ListItemText
                            primary={
                              <CopyableTag
                                text={tag.tagId}
                                variant="text"
                                sx={{ 
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                }}
                              />
                            }
                            secondary={`Scanned at ${new Date(tag.timestamp).toLocaleTimeString()}`}
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label="Unassigned"
                              color="warning"
                              size="small"
                              sx={{ fontSize: '0.625rem' }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: '#6b7280',
                      textAlign: 'center',
                      py: 2
                    }}>
                      No unassigned RFID tags were found
                    </Typography>
                  )}
                </Box>
              )}

              {detailModalTab === 3 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    All Scanned Tags Summary
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Total Tags Scanned: {normalizedSelectedRecord.allScannedTagsList?.length || normalizedSelectedRecord.summary.totalTagsScanned}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Assigned Tags: {normalizedSelectedRecord.summary.assignedTagsCount}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Unassigned Tags: {normalizedSelectedRecord.summary.unassignedTagsCount}
                  </Typography>
                  </Box>
                  
                  {normalizedSelectedRecord.allScannedTagsList && normalizedSelectedRecord.allScannedTagsList.length > 0 ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        All Scanned Tag IDs:
                      </Typography>
                      <Box sx={{ 
                        maxHeight: 300, 
                        overflow: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                        p: 1
                      }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {normalizedSelectedRecord.allScannedTagsList.map((tagId, index) => (
                            <CopyableTag
                              key={index}
                              text={tagId}
                              variant="chip"
                              color={tagId.includes('UNASSIGNED') ? "warning" : "success"}
                              chipVariant={tagId.includes('UNASSIGNED') ? "outlined" : "filled"}
                              size="small"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: '#6b7280',
                      textAlign: 'center',
                      py: 2
                    }}>
                      No detailed tag information available for this count
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseModal}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default InventoryCountHistory;
